import React from 'react';
import { Shield, ShieldAlert, ShieldCheck, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * ComplianceGate — wraps an action element (usually a Button) and:
 *   • Disables it + dims it when the verdict.allowed === false
 *   • Renders a tone-coloured tooltip with the SAMA/PDPL rule + reason
 *   • Shows a tiny inline shield badge that signals state at a glance
 *
 * Usage:
 *   <ComplianceGate verdict={evaluateAction(d, 'call')} onOverride={...}>
 *     <Button onClick={startCall}>Call</Button>
 *   </ComplianceGate>
 */
export const ComplianceGate = ({ verdict, children, onOverride, showBadge = true, className }) => {
  const blocked = verdict && verdict.allowed === false;
  const tone = verdict?.severity?.tone || 'info';

  const Icon = blocked ? Lock : verdict?.severity?.weight > 0 ? ShieldAlert : ShieldCheck;

  // Inject disabled prop on the immediate child if it's a button/element
  const child = React.Children.only(children);
  const gated = blocked
    ? React.cloneElement(child, {
        disabled: true,
        'aria-disabled': true,
        className: cn(child.props.className, 'opacity-50 cursor-not-allowed pointer-events-none'),
      })
    : child;

  return (
    <TooltipProvider delayDuration={120}>
      <div className={cn('inline-flex items-center gap-1.5 relative', className)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">{gated}</span>
          </TooltipTrigger>
          {verdict && (
            <TooltipContent side="top" className="max-w-xs">
              <div className="space-y-1">
                <div className="flex items-center gap-1.5">
                  <Icon className={cn(
                    'h-3.5 w-3.5',
                    tone === 'destructive' ? 'text-destructive' : tone === 'warning' ? 'text-warning' : 'text-success'
                  )} />
                  <p className="text-[11px] font-semibold uppercase tracking-wider">
                    {verdict.rule || 'Compliance'}
                  </p>
                </div>
                <p className="text-xs leading-relaxed">{verdict.reason}</p>
                {blocked && onOverride && (
                  <button
                    onClick={onOverride}
                    className="mt-1 text-[10px] uppercase tracking-widest text-primary underline-offset-2 hover:underline"
                  >
                    Request supervisor override
                  </button>
                )}
              </div>
            </TooltipContent>
          )}
        </Tooltip>
        {showBadge && verdict && verdict.severity.weight > 0 && (
          <span
            className={cn(
              'pointer-events-none absolute -top-1 -right-1 h-2 w-2 rounded-full ring-2 ring-background',
              tone === 'destructive' ? 'bg-destructive' : tone === 'warning' ? 'bg-warning' : 'bg-success'
            )}
          />
        )}
      </div>
    </TooltipProvider>
  );
};

/**
 * ComplianceRow — compact verdict line for lists / NBA panels.
 */
export const ComplianceRow = ({ verdict, label }) => {
  if (!verdict) return null;
  const blocked = !verdict.allowed;
  const Icon = blocked ? Lock : verdict.severity.weight > 0 ? ShieldAlert : Shield;
  return (
    <div className="flex items-start gap-2 text-[11px]">
      <Icon className={cn(
        'h-3.5 w-3.5 mt-0.5 shrink-0',
        blocked ? 'text-destructive' : verdict.severity.weight > 0 ? 'text-warning' : 'text-success'
      )} />
      <div className="leading-tight">
        <p className="font-medium">{label || verdict.rule}</p>
        <p className="text-muted-foreground">{verdict.reason}</p>
      </div>
    </div>
  );
};

export default ComplianceGate;
